// Change history (audit_log, sql/audit-log.sql): plain-words text for one change, CSV rows, field search.
// Shared by the Change history page, the History link on records and the AI tool (api/_agent.js) - no browser APIs here.

import { PO_LABEL } from './poCore.js'
import { TXN_LABEL, CAT_LABEL, KIND_LABEL, CHARGED_LABEL } from './apaCore.js'
import { PERM_MODULES, LEVEL_LABEL, roleLabel } from './permModules.js'

// tables with the audit trigger (same list as sql/audit-log.sql section 5 + sql/consistency.sql section 8 + sql/test-fixes-1.sql section 4) -> what one record of it is called
export const TABLE_LABEL = {
  equipment: 'Equipment', maintenance_tasks: 'Maintenance task', maintenance_log: 'Maintenance log entry', inventory: 'Inventory item',
  crew: 'Crew member', crew_docs: 'Crew certificate', documents: 'Vessel document', hours_rest: 'Hours of Rest',
  day_log: 'Day log entry', engine_log: 'Engine log entry', running_log: 'Running log entry', oil_record_book: 'Oil Record Book entry',
  drill_records: 'Drill', lsa_items: 'LSA / FFE item', lsa_checks: 'LSA / FFE check', checklists: 'Checklist', checklist_items: 'Checklist step',
  checklist_instances: 'Checklist run', risk_assessments: 'Risk assessment', purchase_orders: 'Purchase order', purchase_order_lines: 'Purchase order line',
  charter_apa: 'APA', apa_accounts: 'APA account', apa_transactions: 'APA entry', petty_cash_vouchers: 'Petty cash voucher', apa_gratuities: 'Crew gratuity',
  budget_lines: 'Budget line', budget_actuals: 'Budget actual', todo_lists: 'To Do list', todo_items: 'To Do task', charters: 'Charter',
  officer_monthly_reviews: 'Monthly review', watch_schedules: 'Watch schedule', duty_rotas: 'Duty rota', crew_leave: 'Crew leave', bunkerings: 'Bunkering',
  crew_accounts: 'Crew login', partner_keys: 'Partner key', partner_logins: 'Partner vessel login',
  clients: 'Vessel settings', vessel_particulars: 'Vessel particulars', ism_settings: 'ISM settings',
  // sql/change-history-2.sql
  suppliers: 'Supplier', po_settings: 'Purchase order settings', wishlist_items: 'Wishlist line', passage_plans: 'Passage plan', port_calls: 'Port call',
  marina_bookings: 'Marina booking', refit_items: 'Refit item', diary_entries: 'Diary entry', calendar_events: 'Calendar event', announcements: 'Crew board notice',
  crew_log: 'Crew log entry', crew_list: 'Crew list', crew_movements: 'Crew movement', companies: 'Management company', email_settings: 'E-mail settings',
  partner_connections: 'Partner connection', manuals: 'Manual', vessel_documents: 'Vessel document (SMS)', regulation_reviews: 'Regulation review',
  // sql/feat-10b.sql
  vessel_surveys: 'Survey', survey_checklist_lines: 'Own survey checklist line', vessel_survey_checks: 'Survey check (tick / N/A)',
  checklist_templates: 'Checklist template (shared)', checklist_template_items: 'Checklist template step (shared)', port_contacts: 'Port contact',
  // sql/ptw.sql
  permits: 'Permit to work', permit_isolations: 'Permit isolation', permit_gas_tests: 'Permit gas test', permit_signons: 'Permit crew sign-on', permit_types: 'Permit type', ptw_jobs: 'Job (Start job)',
}
export const AUDITED = new Set(Object.keys(TABLE_LABEL))
export const AREA_LABEL = {
  equipment: 'Equipment', maintenance: 'Maintenance', inventory: 'Inventory', crew: 'Crew', documents: 'Documents', hours_rest: 'Hours of Rest',
  logbooks: 'Log Books', checklists: 'Checklists & Drills', lsa: 'LSA / FFE', risk: 'Risk Assessments', purchase_orders: 'Purchase Orders',
  apa: 'APA', budget: 'Budget', todo: 'To Do', charters: 'Charters',
  officer_review: 'Monthly Review', watch_schedule: 'Watch Schedule', duty_rota: 'Duty Rota', rotations: 'Rotations / Leave', logins: 'Crew Logins / Partner keys',
  vessel: 'Vessel settings / particulars', ism_audits: 'ISM Audits',
  passage: 'Passage Planning', port_calls: 'Port Calls', marina: 'Marina Bookings', refit: 'Refit', diary: 'Diary', calendar: 'Calendar',
  announcements: 'Crew Board', manuals: 'Manuals', regulations: 'Regulations', ptw: 'Permits to Work',
}
// the History window's line for a crew login without Full change history; assigned = the record is assigned to them
// (audit_assigned_to_me(), sql/test-fixes-1.sql), so the read rule shows everyone's changes of it
export function historyScopeText(table, assigned) {
  if (!assigned) return 'Only your own changes are shown.'
  return (table === 'todo_items' ? 'Changes to this task' : 'Changes to this record') + ' - everyone\u2019s, because it is assigned to you.'
}
export const SOURCE_LABEL = { app: 'App', ai: 'AI assistant', script: 'System' }
export const ACTION_LABEL = { insert: 'Added', update: 'Changed', delete: 'Deleted' }

const FIELD_LABEL = {
  vessel_name: 'Vessel name', timezone: 'Time zone', mmsi: 'MMSI', plan: 'Plan', sub_status: 'Subscription', trial_ends: 'Trial ends', renews_on: 'Renews on',
  features: 'Modules switched on', company_id: 'Management company', imo: 'IMO', flag: 'Flag', gross_tonnage: 'GT', net_tonnage: 'NT', call_sign: 'Call sign',
  home_port: 'Home port', year_built: 'Year built', internal_interval_months: 'Internal audit every (months)', warn_days: 'Warn days before', ism_scope: 'ISM scope',
  expiry_date: 'Expiry date', issued_date: 'Issue date', service_date: 'Service date', next_service: 'Next service', survey_due: 'Survey due',
  next_service_date: 'Next service date', next_service_hours: 'Next service hours', last_service_date: 'Last service date', last_service_hours: 'Last service hours',
  current_hours: 'Running hours', critical_flag: 'Critical', min_stock: 'Minimum stock', part_uid: 'Part ID', uid: 'ID', doc_id: 'Document number',
  file_url: 'File', pdf_url: 'PDF', report_url: 'Report', photo_url: 'Photo', crew_present: 'Crew present', drill_date: 'Drill date', drill_type: 'Drill type',
  step_completion: 'Ticked steps', step_notes: 'Step notes', general_notes: 'General notes', completed_at: 'Completed', signed_by_master: 'Signed by Master',
  signed_by_crew: 'Signed by crew', onboard: 'On board', ra_number: 'RA number', next_review_due: 'Next review due', date_of_assessment: 'Date of assessment', visible_to: 'Visible to', ai_generated: 'AI draft',
  annual_budget: 'Annual budget', quantity_m3: 'Quantity (m³)', master_signed_by: 'Master signature', due_date: 'Due date', assignee: 'Assigned to',
  done: 'Done', passport_no: 'Passport number', passport_expiry_date: 'Passport expiry', contract_end_date: 'Contract end', out_of_service_since: 'Out of service since',
  frequency_days: 'Every (days)', frequency_hours: 'Every (hours)', equipment_uid: 'Equipment', zone_id: 'GA zone', deck_id: 'GA deck', pin_x: 'GA pin', pin_y: 'GA pin',
  days: 'Days', txn_date: 'Date', txn_type: 'Type', fx_rate: 'Exchange rate', po_number: 'PO number', needed_by: 'Needed by', delivery_port: 'Delivery port',
  survey_type: 'Survey type', survey_date: 'Survey date', surveyor: 'Surveyor', organisation: 'Organisation', place: 'Place', result: 'Result',
  report_doc_id: 'Report', next_due: 'Next due', readiness_pct: 'Readiness (%)', readiness_summary: 'Readiness', line_id: 'Checklist line', state: 'Tick',
  contact_name: 'Contact', port_name: 'Port', vhf: 'VHF', step_order: 'Step',
}
export function fieldLabel(f) {
  if (FIELD_LABEL[f]) return FIELD_LABEL[f]
  const s = String(f || '').replace(/_/g, ' ').trim()
  return s.charAt(0).toUpperCase() + s.slice(1)
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const dmy = (y, m, d) => Number(d) + ' ' + MONTHS[Number(m) - 1] + ' ' + y
// one value in plain words (dates as 30 Sep 2026, yes / no, lists counted, long text cut)
export function fmtValue(v, max = 140) {
  if (v === null || v === undefined || v === '') return '(empty)'
  if (typeof v === 'boolean') return v ? 'Yes' : 'No'
  if (typeof v === 'number') return String(v)
  if (Array.isArray(v)) return v.length ? (v.every(x => x == null || typeof x !== 'object') ? cut(v.join(', '), max) : v.length + (v.length === 1 ? ' item' : ' items')) : '(none)'
  if (typeof v === 'object') return cut(Object.entries(v).map(([k, x]) => k + ': ' + (x !== null && typeof x === 'object' ? JSON.stringify(x) : fmtValue(x, 40))).join('; '), max)
  const s = String(v)
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (m) return dmy(m[1], m[2], m[3])
  m = s.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/)
  if (m) return dmy(m[1], m[2], m[3]) + ' ' + m[4] + ':' + m[5]
  if (/^https?:\/\//.test(s)) return 'file ' + decodeURIComponent(s.split('/').pop().split('?')[0]).slice(0, 60)
  return cut(s, max)
}
const cut = (s, n) => (s.length > n ? s.slice(0, n - 1) + '…' : s)

// stored codes shown as the pages show them (To Do priority 3 -> High, PO status partially_delivered -> Part delivered ...):
// table.field -> { code: label } or a function. Used by every place the history appears (History window, Change history page +
// its CSV / PDF, the AI's answers), all through changeLines() / sentence() / csvRows() below.
const TODO_PRIORITY = { 0: 'None', 1: 'Low', 2: 'Medium', 3: 'High' }   // as src/pages/ToDo.jsx
const LSA_CHECK = { ok: 'OK', caution: 'Caution', defect: 'Defect', na: 'N/A' }
const WISH_STATUS = { open: 'Open', ordered: 'Ordered', restocked: 'Restocked', cancelled: 'Not needed' }   // as src/pages/POWishlist.jsx
export const VALUE_LABEL = {
  'todo_items.priority': TODO_PRIORITY,
  'purchase_orders.status': PO_LABEL,
  'apa_transactions.txn_type': TXN_LABEL, 'apa_transactions.category': CAT_LABEL, 'petty_cash_vouchers.charged_to': CHARGED_LABEL,
  'petty_cash_vouchers.category': CAT_LABEL, 'apa_accounts.kind': KIND_LABEL,
  'lsa_checks.status': LSA_CHECK,
  'wishlist_items.status': WISH_STATUS,
  'crew_accounts.role': v => roleLabel(v),
}
// one stored value as the page shows it (unchanged when there is no label for it)
export function valueLabel(table, field, v) {
  const m = VALUE_LABEL[table + '.' + field]
  if (v === null || v === undefined || v === '' || !m) return v
  if (typeof m === 'function') return m(v)
  return Object.prototype.hasOwnProperty.call(m, String(v)) ? m[String(v)] : v
}
const MODULE_NAME = Object.fromEntries(PERM_MODULES)

// the changed fields of one log entry as [{ field, label, old, new, text }]
export function changeLines(e) {
  const ch = (e && e.changes) || {}
  return Object.entries(ch).map(([field, p]) => {
    p = p || {}
    const label = fieldLabel(field)
    if (p.part) {
      // a JSON field (e.g. Hours of Rest days): only the keys that changed
      const keys = [...new Set([...Object.keys(p.old || {}), ...Object.keys(p.new || {})])].sort()
      // a login's permissions: "Equipment: View → Edit" (as the Crew Logins page shows them)
      const text = field === 'permissions'
        ? label + ': ' + keys.map(k => (MODULE_NAME[k] || fmtValue(k)) + ' ' + fmtValue(LEVEL_LABEL[(p.old || {})[k]] || (p.old || {})[k]) + ' → ' + fmtValue(LEVEL_LABEL[(p.new || {})[k]] || (p.new || {})[k])).join(', ')
        : label + ': ' + keys.map(k => fmtValue(k) + ' ' + (!(p.old || {}).hasOwnProperty(k) ? 'added' : !(p.new || {}).hasOwnProperty(k) ? 'removed' : 'changed')).join(', ')
      return { field, label, old: p.old, new: p.new, keys, text }
    }
    const o = valueLabel(e.table_name, field, p.old), n = valueLabel(e.table_name, field, p.new)
    const text = e.action === 'insert' ? label + ': ' + fmtValue(n)
      : e.action === 'delete' ? label + ' was ' + fmtValue(o)
      : label + ': ' + fmtValue(o) + ' → ' + fmtValue(n)
    return { field, label, old: o, new: n, text }
  })
}
export const recordName = e => (e.record_label || TABLE_LABEL[e.table_name] || e.table_name || 'record')
// one line: Tom (chief officer) changed LSA / FFE item "Liferaft 1": Expiry date: 30 Sep 2026 -> 30 Sep 2027
export function sentence(e, { maxFields = 6 } = {}) {
  const what = (TABLE_LABEL[e.table_name] || e.table_name) + ' "' + recordName(e) + '"'
  const verb = e.action === 'insert' ? 'added' : e.action === 'delete' ? 'deleted' : 'changed'
  const lines = e.action === 'update' ? changeLines(e) : []
  const more = lines.length > maxFields ? ' (+' + (lines.length - maxFields) + ' more)' : ''
  return (e.user_name || 'Unknown user') + (e.source === 'ai' ? ' (through the AI assistant)' : '') + ' ' + verb + ' ' + what
    + (lines.length ? ': ' + lines.slice(0, maxFields).map(l => l.text).join('; ') + more : '')
    + (e.comment ? ' - comment: "' + e.comment + '"' : '')
    + (e.offline_at ? ' - ' + offlineNote(e) : '')
}
// a change made offline and synced later (sql/offline-1.sql): when it was made (device clock) and when it arrived
export const offlineNote = e => (e && e.offline_at ? 'made offline ' + utc(e.offline_at) + ' (synced ' + utc(e.at) + ')' : '')
// UTC, so the record reads the same wherever it is opened
export const utc = at => { const d = new Date(at); return isNaN(d) ? '' : d.toISOString().slice(0, 16).replace('T', ' ') + ' UTC' }

export const CSV_HEADER = ['Date / time (UTC)', 'Person', 'Source', 'Area', 'Record type', 'Record', 'Action', 'Field', 'Old value', 'New value', 'Made offline (UTC, device clock)', 'Comment']
// one CSV row per changed field (an add / delete without fields still gets one row)
export function csvRows(entries) {
  const out = []
  for (const e of entries || []) {
    const base = [utc(e.at), e.user_name || '', SOURCE_LABEL[e.source] || e.source || '', AREA_LABEL[e.area] || e.area || '', TABLE_LABEL[e.table_name] || e.table_name || '', recordName(e), ACTION_LABEL[e.action] || e.action]
    const lines = changeLines(e)
    const off = e.offline_at ? utc(e.offline_at) : ''
    if (!lines.length) out.push([...base, '', '', '', off, e.comment || ''])
    for (const l of lines) out.push([...base, l.label, l.old === undefined ? '' : fmtValue(l.old, 2000), l.new === undefined ? '' : fmtValue(l.new, 2000), off, e.comment || ''])
  }
  return out
}
export function toCsv(rows, header = CSV_HEADER) {
  const q = v => { const s = v == null ? '' : String(v); return /[",\n;]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s }
  return '﻿' + [header, ...rows].map(r => r.map(q).join(',')).join('\r\n')
}

// does this entry touch a field the user named? ("expiry" -> expiry_date, "service" -> service_date / next_service)
const norm = s => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()
export function touchesField(e, words) {
  const w = norm(words).replace(/\b(date|the|of|a)\b/g, ' ').trim().split(/\s+/).filter(Boolean)
  if (!w.length) return true
  return Object.keys((e && e.changes) || {}).some(f => { const h = norm(f) + ' ' + norm(fieldLabel(f)); return w.every(x => h.includes(x.replace(/s$/, ''))) })
}
// does the record name / type match the user's words? ("liferaft" -> "Liferaft 1 (12 person)")
export function matchesRecord(e, words) {
  const w = norm(words).split(/\s+/).filter(Boolean)
  if (!w.length) return true
  const h = norm(recordName(e) + ' ' + (TABLE_LABEL[e.table_name] || '') + ' ' + (AREA_LABEL[e.area] || ''))
  return w.every(x => h.includes(x.replace(/s$/, '')))
}
// the same entry with only the fields the user asked about (when any match)
export function onlyFields(e, words) {
  const keep = Object.entries((e && e.changes) || {}).filter(([f]) => touchesField({ changes: { [f]: 1 } }, words))
  return keep.length ? { ...e, changes: Object.fromEntries(keep) } : e
}
